import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApi, API, errMsg } from "@/lib/api";

export const uploadProof = (api, sid, file, notify = false) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("notify", notify ? "true" : "false");
  return api("post", `/admin/shipments/${sid}/proof`, fd);
};

export const ProofInput = ({ onChange, testId }) => (
  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => onChange(e.target.files?.[0] || null)}
    className="block w-full text-sm file:mr-3 file:px-4 file:py-2 file:border-0 file:rounded-sm file:bg-[#0A192F] file:text-white file:font-semibold file:cursor-pointer" data-testid={testId} />
);

export function ProofDialog({ open, onOpenChange, shipment, onSaved }) {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(true);
  useEffect(() => { if (open) { setFile(null); setNotify(true); } }, [open]);
  const current = shipment?.proof_photo;
  const email = shipment?.receiver?.email;

  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(r?.email === "sent" ? `${msg}. Receiver emailed with the photo.` : r?.email?.startsWith("failed") ? `${msg}. Email ${r.email}.` : msg); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="proof-dialog">
        <DialogHeader><DialogTitle className="font-display">Proof of delivery · <span className="font-mono text-[#FF6B00]">{shipment?.tracking_number}</span></DialogTitle></DialogHeader>
        {current && <img src={`${API}/proof/${current.id}`} alt="Current proof of delivery" className="w-full max-h-64 object-cover rounded-sm border" data-testid="proof-current-img" />}
        <ProofInput onChange={setFile} testId="proof-file-input" />
        <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
          <div><p className="text-sm font-medium">Email the photo to the receiver</p><p className="text-xs text-slate-500">{email || "No receiver email on file"}</p></div>
          <Switch checked={!!email && notify} onCheckedChange={setNotify} disabled={!email} data-testid="proof-notify-switch" />
        </div>
        <div className="flex gap-2">
          <Button disabled={!file || busy} onClick={() => run(() => uploadProof(api, shipment.id, file, !!email && notify), "Proof photo saved")} className="flex-1 bg-[#FF6B00] hover:bg-[#E05D00]" data-testid="proof-upload-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {current ? "Replace photo" : "Upload photo"}
          </Button>
          {current && <Button variant="outline" disabled={busy} onClick={() => run(() => api("delete", `/admin/shipments/${shipment.id}/proof`), "Proof photo removed")} data-testid="proof-remove-btn"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
